const SHOP_ENTRY_KEYS = [
    'exclusiveEntries',
    'midnightEntries',
    'blackmarketEntries',
    'eventLimitedEntries',
];

const DUNGEON_TRANSITION_EFFECT = /^(?:teleport_(?:dungeon|labyrinth|maze)(?:_|$)|start_eternal_vault$)/;

const normalizeId = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * ゲーム内アイテムショップで販売してはいけない商品を判定する。
 * 課金プレゼント専用ペットと迷宮遷移は常に禁止。帰還印は現行ゲームに
 * 実在する mapId を指すものだけを許可する。
 */
export function forbiddenShopItemReason(itemDef, allowedMapIds = []) {
    if (!itemDef || typeof itemDef !== 'object') return '';
    const itemId = normalizeId(itemDef.id);
    const effect = normalizeId(itemDef.effect);
    if (itemDef.shopForbidden === true) return 'shopForbidden=true の非売品';
    if (itemId.startsWith('pet_')
        || normalizeId(itemDef.type) === 'pet'
        || normalizeId(itemDef.category) === 'pet'
        || effect === 'buff_summon_pet'
        || normalizeId(itemDef.summonPetId)
        || normalizeId(itemDef.petId)) {
        return '課金プレゼント専用のペットアイテム';
    }
    if (DUNGEON_TRANSITION_EFFECT.test(effect)) {
        return '迷宮の入退場に関するテレポートアイテム';
    }
    if (effect === 'teleport_city') {
        const targetMapId = normalizeId(itemDef.targetMapId);
        const allowed = new Set(Array.from(allowedMapIds ?? [], normalizeId).filter(Boolean));
        if (!targetMapId || !allowed.has(targetMapId)) {
            return `実在しない別ゲームの帰還先 mapId「${targetMapId || '未設定'}」`;
        }
    }
    return '';
}

function filterEntries(entries, itemById, allowedMapIds, removed) {
    if (!Array.isArray(entries)) return entries;
    return entries.filter((entry) => {
        const itemId = normalizeId(entry?.itemId);
        if (entry?.kind === 'equipment' && !itemById.has(itemId)) return true;
        const itemDef = itemById.get(itemId) ?? { id: itemId };
        const reason = forbiddenShopItemReason(itemDef, allowedMapIds);
        if (!reason) return true;
        removed.push({ itemId, reason });
        return false;
    });
}

/** JSON 内の販売エントリだけを破壊的に正規化する（配布・所持定義は残す）。 */
export function sanitizeShopSales({ shops = null, shopMaster = null, items = null, allowedMapIds = [] } = {}) {
    const itemById = new Map((Array.isArray(items?.items) ? items.items : [])
        .filter((item) => item?.id)
        .map((item) => [normalizeId(item.id), item]));
    const removed = [];

    for (const profile of Array.isArray(shops?.shopProfiles) ? shops.shopProfiles : []) {
        for (const key of SHOP_ENTRY_KEYS) {
            if (Array.isArray(profile?.[key])) {
                profile[key] = filterEntries(profile[key], itemById, allowedMapIds, removed);
            }
        }
    }

    const pools = shopMaster?.commonPools;
    if (Array.isArray(pools)) {
        for (const pool of pools) {
            if (Array.isArray(pool?.entries)) {
                pool.entries = filterEntries(pool.entries, itemById, allowedMapIds, removed);
            }
        }
    } else if (pools && typeof pools === 'object') {
        for (const [poolId, entries] of Object.entries(pools)) {
            if (Array.isArray(entries)) {
                pools[poolId] = filterEntries(entries, itemById, allowedMapIds, removed);
            }
        }
    }

    return { shops, shopMaster, removed };
}

/**
 * そのゲームのアイテムショップで実際に購入できる itemId の集合を返す。
 *
 * 販売枠（通常・深夜・闇市・イベント限定）と、各プロファイルが参照する共通プール
 * （shop-master.json commonPools）をすべて合わせる。時間帯・エリア条件で「いつでも」
 * 買えるとは限らないが、「入手経路がショップに存在するか」の判定にはこれで足りる。
 *
 * 装備枠（kind=equipment）は items.json のアイテムではないので含めない。
 */
export function collectPurchasableItemIds({ shops = null, shopMaster = null } = {}) {
    const ids = new Set();
    const addEntries = (entries) => {
        for (const entry of Array.isArray(entries) ? entries : []) {
            if (entry?.kind === 'equipment') continue;
            const itemId = normalizeId(entry?.itemId);
            if (itemId) ids.add(itemId);
        }
    };

    const usedPoolIds = new Set();
    for (const profile of Array.isArray(shops?.shopProfiles) ? shops.shopProfiles : []) {
        for (const key of SHOP_ENTRY_KEYS) addEntries(profile?.[key]);
        for (const poolId of Array.isArray(profile?.commonPoolIds) ? profile.commonPoolIds : []) {
            const id = normalizeId(poolId);
            if (id) usedPoolIds.add(id);
        }
    }

    const pools = shopMaster?.commonPools;
    if (Array.isArray(pools)) {
        for (const pool of pools) {
            if (usedPoolIds.has(normalizeId(pool?.id))) addEntries(pool?.entries);
        }
    } else if (pools && typeof pools === 'object') {
        for (const [poolId, entries] of Object.entries(pools)) {
            if (!usedPoolIds.has(normalizeId(poolId))) continue;
            addEntries(Array.isArray(entries) ? entries : entries?.entries);
        }
    }
    return ids;
}
